import { Data } from '.generated/templates/Feature.Data.model';
import { Routes } from '.generated/templates/Project.Routes.model';
import { useSitecoreContext, Text, ImageField } from '@sitecore-jss/sitecore-jss-react';
import classnames from 'tailwindcss-classnames';
import Button from 'components/helpers/Button';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import useDictionary from 'lib/sitecore/use-dictionary';
import {
  addToCalendar,
  getFormattedEndTime,
  getFormattedEventDate,
  getFormattedStartTime,
} from 'lib/util/event';
import { LayoutPageSectionShell } from 'components/authorable/layout/LayoutPageSection/LayoutPageSection';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import getThemeClasses from 'lib/util/theming';
import ImageWrapper from 'components/helpers/ImageWrapper/ImageWrapper';
import VideoItem, { VideoItemProps } from 'components/helpers/VideoItem/VideoItem';
import { DateTime } from 'luxon';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';
import ImageCaption from 'components/helpers/ImageCaption/ImageCaption';

export type EventPageProps = Routes.Fields.EventPage & {
  fields: {
    eventCategory?: Data.Events.Fields.EventCategory[];
    pageFeaturedVideo?: VideoItemProps;
  };
};

const EventPage = (): JSX.Element => {
  const sitecoreContext = useSitecoreContext();
  const dictionary = useDictionary();
  const { fields } = sitecoreContext.sitecoreContext.route as unknown as EventPageProps;
  const ContentCheck = JSON.stringify(sitecoreContext.sitecoreContext.route?.placeholders);
  const ifContentBlockExists = ContentCheck.includes('ContentBlock');
  // Fail out if we don't have any fields
  if (!fields) {
    return <MissingDataSource />;
  }
  const featuredImage = fields.pageFeaturedImage.value?.src
    ? fields.pageFeaturedImage
    : (sitecoreContext.sitecoreContext.defaultFeaturedImage as ImageField);
  let eventExpired = false;
  let monthisless = false;

  const occursAnnually = fields?.occursAnnually?.value;
  if (occursAnnually) {
    const todayDate = DateTime.now();

    const startDate = DateTime.fromISO(fields.startDateTime.value);
    const endDate = DateTime.fromISO(fields.endDateTime.value);
    if (startDate < todayDate && endDate < todayDate) {
      eventExpired = true;
      if (startDate.month <= todayDate.month) {
        monthisless = true;
      }
    }
  }

  const eventCategories = fields.eventCategory.map((item: unknown, index: unknown) => {
    const eventCategory = item as Data.Events.Fields.EventCategory;
    const eventItem = item as Sitecore.Override.ItemEx;
    return (
      <span key={eventItem.id}>{`${index ? ',' : ''} ${
        eventCategory.fields?.eventCategoryTitle.value
      }`}</span>
    );
  });

  let startDate = fields.startDateTime.value;
  let endDate = fields.endDateTime.value;

  if (eventExpired) {
    let year = 0;
    const todayDate = DateTime.now();
    if (monthisless) {
      year = todayDate.year + 1;
    } else {
      year = todayDate.year;
    }
    startDate = year.toString().concat(startDate.slice(4));
    endDate = year.toString().concat(endDate.slice(4));
  }

  const addToCalendarHandler = async () => {
    addToCalendar(
      fields.pageTitle.value,
      fields.pageDescription.value,
      startDate,
      endDate,
      fields.location.value,
      fields.eventCategory
    );
  };

  const eventDateHeading = dictionary.Event_Date;
  const eventLocationHeading = dictionary.Event_Location;
  const eventTimeHeading = dictionary.Event_Time;
  const addToCalendarText = dictionary.Add_To_Calendar;

  const formattedEventDate = getFormattedEventDate(startDate, endDate);

  const formattedStartTime = getFormattedStartTime(startDate, endDate);
  const formattedEndTime = getFormattedEndTime(endDate);

  const eventSection = classnames('flex', 'py-2');
  const eventSectionLabel = classnames('text-blue-navy', 'w-1/3');
  const eventSectionItem = classnames('w-2/3');

  const eventContainer = (
    <>
      <div
        className={classnames(
          'border',
          'border-gray-quartz',
          'px-2.5',
          'py-4',
          'rounded-lg',
          'text-base',
          'sm:text-body',
          'sm:pr-4'
        )}
      >
        <div className={classnames(eventSection)}>
          <span className={classnames(eventSectionLabel)}> {eventDateHeading}</span>
          <span className={classnames(eventSectionItem)}>{formattedEventDate}</span>
        </div>
        <div className={classnames(eventSection)}>
          <span className={classnames(eventSectionLabel)}>{eventTimeHeading}</span>
          <span className={classnames(eventSectionItem)}>
            {formattedStartTime}
            {formattedEndTime}
          </span>
        </div>
        <div className={classnames(eventSection)}>
          <span className={classnames(eventSectionLabel)}>{eventLocationHeading}</span>
          <address className={classnames(eventSectionItem, 'break-words')}>
            <Text field={fields.location} className={classnames('whitespace-pre-line')} />
          </address>
        </div>
        <hr className={classnames('my-2')} />
        <div className={classnames(eventSection)}>
          <span className={classnames(eventSectionLabel)}>
            <Text field={fields.linkLabel1} />
          </span>
          <Button
            className={classnames(eventSectionItem)}
            linkField={fields.link1}
            variant="link"
          />
        </div>
        <div className={classnames(eventSection)}>
          <span className={classnames(eventSectionLabel)}>
            <Text field={fields.linkLabel2} />
          </span>
          <Button
            className={classnames(eventSectionItem)}
            linkField={fields.link2}
            variant="link"
          />
        </div>
        <div className={classnames(eventSection)}>
          <span className={classnames(eventSectionLabel)}>
            <Text field={fields.linkLabel3} />
          </span>
          <Button
            className={classnames(eventSectionItem)}
            linkField={fields.link3}
            variant="link"
          />
        </div>
      </div>
      <div>
        <Button
          className={classnames('w-full', 'my-6')}
          text={addToCalendarText}
          onClick={addToCalendarHandler}
          variant="secondary"
        />
      </div>
    </>
  );
  const titleContainer = (
    <>
      <div
        className={classnames(
          'heading-xs-extendable',
          'text-gray-manatee',
          'font-semibold',
          'py-3'
        )}
      >
        {eventCategories}
      </div>
      <h1 className={classnames('text-blue-navy', 'heading-lg-extendable', 'pb-6', 'md:pb-10')}>
        {/* {fields.pageTitle.value} */}
        <Text field={fields.pageTitle} />
      </h1>
    </>
  );

  return (
    <div
      data-component="authorable/pages/eventpage"
      className={classnames('page-section-gap-closer', getThemeClasses('theme-white'))}
    >
      <LayoutPageSectionShell theme="theme-inherit" padding="tiny">
        <div
          className={classnames(
            'flex',
            'flex-wrap',
            'flex-col',
            'sm:grid',
            'md:grid-cols-2',
            'lg:grid-cols-3'
          )}
        >
          <div className={classnames('order-1', 'sm:order-none', 'col-span-2', 'lg:col-span-3')}>
            {titleContainer}
          </div>
          {!fields.pageFeaturedVideo ? (
            <div
              className={classnames('image-block', 'col-span-2', 'md:col-span-1', 'lg:col-span-2')}
            >
              <ImageWrapper field={featuredImage} />
              <ImageCaption
                imageCaption={fields.featuredImageCaption}
                classNames={classnames('px-4')}
              />
            </div>
          ) : (
            <div className={classnames('col-span-2', 'md:col-span-1', 'lg:col-span-2')}>
              <VideoItem {...fields.pageFeaturedVideo} />
            </div>
          )}
          <div className={classnames('col-span-2', 'md:col-span-1', 'md:pl-10')}>
            {eventContainer}
          </div>
        </div>
        {!ifContentBlockExists && (
          <RichTextA11yWrapper
            className={classnames('rte', 'leading-5', 'pt-0', 'lg:pt-6', 'text-wrap')}
            field={fields.pageDescription}
          />
        )}
      </LayoutPageSectionShell>
    </div>
  );
};

export default EventPage;
